class StaticSearchForm {
  constructor() {
    this.mapboxToken = window.MB_TOKEN || '';
    this.formButtons = document.querySelectorAll('.js-form-button');
    this.minPriceSelect = document.querySelector('.js-min-price');
    this.maxPriceSelect = document.querySelector('.js-max-price');
    this.minBedroomsSelect = document.querySelector('.js-min-bedrooms');
    this.maxBedroomsSelect = document.querySelector('.js-max-bedrooms');
    this.searchInput = document.querySelector('.js-search-input');
    this.resultsDiv = document.querySelector('.js-search-results');
    this.latField = document.querySelector('.js-lat');
    this.lonField = document.querySelector('.js-lng');

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        this.handleSearch(e.target.value);
      });

      this.searchInput.addEventListener('blur', (e) => {
        setTimeout(() => {
          if (!this.resultsDiv.contains(document.activeElement)) {
            this.resultsDiv.innerHTML = '';
          }
        }, 100);
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (!this.resultsDiv || this.resultsDiv.children.length === 0) return;
        const items = Array.from(this.resultsDiv.children);
        const activeIndex = items.findIndex((item) =>
          item.classList.contains('active')
        );
        let nextIndex = activeIndex;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          nextIndex = activeIndex < items.length - 1 ? activeIndex + 1 : 0;
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          nextIndex = activeIndex > 0 ? activeIndex - 1 : items.length - 1;
        } else if (e.key === 'Enter') {
          if (activeIndex >= 0) {
            e.preventDefault();
            items[activeIndex].click();
          }
        } else if (e.key === 'Escape') {
          this.resultsDiv.innerHTML = '';
          this.resultsDiv.setAttribute('hidden', '');
        } else if (e.key === 'Tab') {
          this.resultsDiv.innerHTML = '';
          this.resultsDiv.setAttribute('hidden', '');
        }
        if (nextIndex !== activeIndex) {
          items.forEach((item, idx) => {
            item.setAttribute('aria-selected', 'false');
            item.tabIndex = -1;
          });
          const focused = items[nextIndex];
          if (focused) {
            focused.setAttribute('aria-selected', 'true');
            focused.tabIndex = 0;
            focused.focus();
          }
        }
      });
    }

    if (this.resultsDiv) {
      this.resultsDiv.addEventListener(
        'blur',
        (e) => {
          setTimeout(() => {
            if (
              document.activeElement !== this.searchInput &&
              !this.resultsDiv.contains(document.activeElement)
            ) {
              this.resultsDiv.innerHTML = '';
            }
          }, 100);
        },
        true
      );
    }
    if (this.minPriceSelect) {
      this.minPriceSelect.addEventListener('change', () => {
        this.updatePriceButtonText();
        this.updatePriceSelectOptions();
      });
    }
    if (this.maxPriceSelect) {
      this.maxPriceSelect.addEventListener('change', () => {
        this.updatePriceButtonText();
        this.updatePriceSelectOptions();
      });
    }

    if (this.minBedroomsSelect) {
      this.minBedroomsSelect.addEventListener('change', () => {
        this.updateBedroomsButtonText();
        this.updateBedroomSelectOptions();
      });
    }
    if (this.maxBedroomsSelect) {
      this.maxBedroomsSelect.addEventListener('change', () => {
        this.updateBedroomsButtonText();
        this.updateBedroomSelectOptions();
      });
    }

    this.formButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleDropdownClick(button);
      });

      button.addEventListener('blur', (e) => {
        setTimeout(() => {
          const dropdown = button.nextElementSibling;
          if (!dropdown || !dropdown.contains(document.activeElement)) {
            this.closeAllDropdowns();
          }
        }, 10);
      });

      const dropdown = button.nextElementSibling;
      if (dropdown && dropdown.tagName === 'DIV') {
        dropdown.addEventListener(
          'blur',
          (e) => {
            setTimeout(() => {
              if (
                !button.contains(document.activeElement) &&
                !dropdown.contains(document.activeElement)
              ) {
                this.closeAllDropdowns();
              }
            }, 10);
          },
          true
        );
      }
    });

    document.addEventListener('click', (e) => {
      this.handleOutsideClick(e);
    });
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  async geocodeAddress(query) {
    if (!query || query.trim().length < 3) {
      return [];
    }

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
      query
    )}.json?access_token=${this.mapboxToken}&limit=5&country=GB`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      return data.features.map((feature) => ({
        name: feature.place_name,
        longitude: feature.center[0],
        latitude: feature.center[1],
        bbox: feature.bbox,
      }));
    } catch (error) {
      console.error('Geocoding error:', error);
      return [];
    }
  }

  handleSearch = this.debounce(async function (query) {
    const results = await this.geocodeAddress(query);
    this.displayResults(results);
    if (results.length > 0) {
      const first = results[0];
      this.selectLocation(first.latitude, first.longitude, null, true);
    }
  }, 300);

  displayResults(results) {
    if (results.length === 0) {
      this.resultsDiv.innerHTML = '';
      this.resultsDiv.setAttribute('hidden', '');
      return;
    }

    this.resultsDiv.innerHTML = '';
    this.resultsDiv.removeAttribute('hidden');
    this.resultsDiv.setAttribute('role', 'listbox');

    Array.from(this.resultsDiv.children).forEach((item) => {
      item.classList.remove('active', 'bg-blue-700', 'text-white');
      item.setAttribute('aria-selected', 'false');
    });

    results.forEach((result, idx) => {
      const resultItem = document.createElement('div');
      resultItem.setAttribute('role', 'option');
      resultItem.setAttribute('tabindex', idx === 0 ? '0' : '-1');
      resultItem.setAttribute('aria-selected', 'false');
      resultItem.className =
        'result-item w-full block px-7 py-4 text-left border-b border-[#EBEBEB] last:border-b-0 group cursor-pointer hover:bg-blue-700 hover:text-white focus:bg-blue-700 focus:text-white';
      const parts = result.name.split(',');
      let html = '';
      if (parts.length > 1) {
        html = `
          <div class="text-sm">
            <div class="font-medium text-black mb-2 group-hover:text-white group-focus:text-white">
              ${parts[0].trim()}
            </div>
            <div class="font-light text-[#6B7280] group-hover:text-white group-focus:text-white">
              ${parts.slice(1).join(',').trim()}
            </div>
          </div>
        `;
      } else {
        html = result.name;
      }
      resultItem.innerHTML = html;

      resultItem.addEventListener('mousedown', (e) => {
        e.preventDefault();
      });
      resultItem.addEventListener('click', () => {
        this.selectLocation(result.latitude, result.longitude, result.name);
      });
      resultItem.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          resultItem.click();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const next = resultItem.nextElementSibling;
          if (next) next.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = resultItem.previousElementSibling;
          if (prev) prev.focus();
        } else if (e.key === 'Escape' || e.key === 'Tab') {
          this.resultsDiv.innerHTML = '';
          this.resultsDiv.setAttribute('hidden', '');
          this.searchInput.focus();
        }
      });

      resultItem.addEventListener('focus', () => {
        Array.from(this.resultsDiv.children).forEach((item) => {
          item.setAttribute('aria-selected', 'false');
          item.classList.remove('active', 'bg-blue-700', 'text-white');
        });
        resultItem.setAttribute('aria-selected', 'true');
        resultItem.classList.add('active', 'bg-blue-700', 'text-white');
      });
      resultItem.addEventListener('blur', () => {
        resultItem.setAttribute('aria-selected', 'false');
        resultItem.classList.remove('active', 'bg-blue-700', 'text-white');
      });

      this.resultsDiv.appendChild(resultItem);
    });
  }

  selectLocation(lat, lon, name, keepOpen = false) {
    if (this.searchInput && name) {
      this.searchInput.value = name;
    }
    if (this.latField) {
      this.latField.value = lat;
    }
    if (this.lonField) {
      this.lonField.value = lon;
    }
    if (this.resultsDiv && !keepOpen) {
      this.resultsDiv.innerHTML = '';
      this.resultsDiv.setAttribute('hidden', '');
    }
  }

  updatePriceButtonText() {
    let priceButton = null;
    if (this.minPriceSelect) {
      const fieldset = this.minPriceSelect.closest('fieldset');
      if (fieldset) {
        priceButton = Array.from(this.formButtons).find((btn) =>
          fieldset.contains(btn)
        );
      }
    }
    if (!priceButton) return;

    const minValue = this.minPriceSelect.value;
    const maxValue = this.maxPriceSelect.value;
    const minText =
      this.minPriceSelect.options[this.minPriceSelect.selectedIndex].text;
    const maxText =
      this.maxPriceSelect.options[this.maxPriceSelect.selectedIndex].text;

    let buttonText = 'Price range';

    if (minValue && maxValue) {
      buttonText = `${minText} - ${maxText}`;
    } else if (minValue) {
      buttonText = `From ${minText}`;
    } else if (maxValue) {
      buttonText = `Up to ${maxText}`;
    }

    const textNode = priceButton.firstChild;
    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
      textNode.textContent = buttonText;
    }
  }

  updateBedroomsButtonText() {
    let bedroomsButton = null;
    if (this.minBedroomsSelect) {
      const fieldset = this.minBedroomsSelect.closest('fieldset');
      if (fieldset) {
        bedroomsButton = Array.from(this.formButtons).find((btn) =>
          fieldset.contains(btn)
        );
      }
    }
    if (!bedroomsButton) return;

    const minValue = this.minBedroomsSelect.value;
    const maxValue = this.maxBedroomsSelect.value;
    const minText =
      this.minBedroomsSelect.options[this.minBedroomsSelect.selectedIndex].text;
    const maxText =
      this.maxBedroomsSelect.options[this.maxBedroomsSelect.selectedIndex].text;

    let buttonText = 'Min beds';

    if (minValue && maxValue) {
      buttonText = `${minText} - ${maxText} bedrooms`;
    } else if (minValue) {
      buttonText = `${minText}+ bedrooms`;
    } else if (maxValue) {
      buttonText = `Up to ${maxText} bedrooms`;
    }

    const textNode = bedroomsButton.firstChild;
    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
      textNode.textContent = buttonText;
    }
  }

  updatePriceSelectOptions() {
    const minValue = parseInt(this.minPriceSelect.value) || 0;
    const maxValue = parseInt(this.maxPriceSelect.value) || Infinity;

    Array.from(this.maxPriceSelect.options).forEach((option) => {
      const optionValue = parseInt(option.value) || 0;
      if (option.value && optionValue < minValue) {
        option.disabled = true;
      } else {
        option.disabled = false;
      }
    });

    Array.from(this.minPriceSelect.options).forEach((option) => {
      const optionValue = parseInt(option.value) || 0;
      if (option.value && maxValue !== Infinity && optionValue > maxValue) {
        option.disabled = true;
      } else {
        option.disabled = false;
      }
    });
  }

  updateBedroomSelectOptions() {
    const minValue = parseInt(this.minBedroomsSelect.value) || 0;
    const maxValue = parseInt(this.maxBedroomsSelect.value) || Infinity;

    Array.from(this.maxBedroomsSelect.options).forEach((option) => {
      const optionValue = parseInt(option.value) || 0;
      if (option.value && optionValue < minValue) {
        option.disabled = true;
      } else {
        option.disabled = false;
      }
    });

    Array.from(this.minBedroomsSelect.options).forEach((option) => {
      const optionValue = parseInt(option.value) || 0;
      if (option.value && maxValue !== Infinity && optionValue > maxValue) {
        option.disabled = true;
      } else {
        option.disabled = false;
      }
    });
  }

  toggleArrow(button, isOpen) {
    const arrow = button.querySelector('svg');
    if (arrow) {
      if (isOpen) {
        arrow.style.transform = 'rotate(180deg)';
      } else {
        arrow.style.transform = 'rotate(0deg)';
      }
    }
  }

  closeAllDropdowns(exceptButton = null) {
    this.formButtons.forEach((button) => {
      if (button !== exceptButton) {
        const dropdown = button.nextElementSibling;
        if (
          dropdown &&
          dropdown.tagName === 'DIV' &&
          !dropdown.hasAttribute('hidden')
        ) {
          dropdown.setAttribute('hidden', '');
          button.setAttribute('aria-expanded', 'false');
          this.toggleArrow(button, false);
        }
      }
    });
  }

  handleDropdownClick(button) {
    const dropdown = button.nextElementSibling;

    if (dropdown && dropdown.tagName === 'DIV') {
      const isHidden = dropdown.hasAttribute('hidden');

      if (isHidden) {
        this.closeAllDropdowns(button);
        dropdown.removeAttribute('hidden');
        button.setAttribute('aria-expanded', 'true');
        this.toggleArrow(button, true);
      } else {
        dropdown.setAttribute('hidden', '');
        button.setAttribute('aria-expanded', 'false');
        this.toggleArrow(button, false);
      }
    }
  }

  handleOutsideClick(e) {
    if (
      !e.target.closest('.js-form-button') &&
      !e.target.closest('fieldset div:not([hidden])')
    ) {
      this.closeAllDropdowns();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new StaticSearchForm();
  var resultsDiv = document.querySelector('.js-search-results');
  if (resultsDiv) {
    resultsDiv.setAttribute('hidden', '');
  }
});
